'use client';

import { logger } from '@adornis/base/logging.js';
import { ImageFile } from '@adornis/file-utils/db/images.js';
import { PDFFile } from '@adornis/pdf/db/pdf-file.js';
import { ArrowLeft, Check, ChevronLeft, ChevronRight, Download, Eye, ZoomIn, ZoomOut } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getMyExamResultQuery } from '../../../db/api/get-my-class-exam-results.js';
import type { ExamResultRelease } from '../../../db/exam-result-release.js';
import { MarkdownWithMath } from '../markdown/markdown-with-math.js';
import { Button } from '../ui/button.js';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog.js';

const MIN_ZOOM = 1;
const MAX_ZOOM = 2.5;
const ZOOM_STEP = 0.25;

interface StudentExamResultPageProps {
  examId: string;
  examTitle?: string | null;
  onBack: () => void;
}

/**
 * A student's own released result for one exam: per task their answer, the points and the feedback.
 *
 * Renders the frozen `ExamResultRelease` snapshot and nothing else — it never reads the exam or the
 * submission, so there is no path here to another student's work, and what is shown is exactly what the
 * teacher released rather than whatever the grading has drifted to since.
 */
export function StudentExamResultPage({ examId, examTitle, onBack }: StudentExamResultPageProps) {
  const { t } = useTranslation();

  const [result, setResult] = useState<ExamResultRelease | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // `null` until the student clicks a page thumbnail or the "view PDF" button — that click is what
  // opens the preview dialog. The thumbnails stay visible on the page throughout, so switching pages is
  // just closing and clicking a different one, or paging with the dialog's own left/right buttons.
  const [activePreview, setActivePreview] = useState<{ kind: 'pdf' } | { kind: 'image'; index: number } | null>(null);
  const [zoom, setZoom] = useState(MIN_ZOOM);

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);

    void getMyExamResultQuery(examId)({
      examTitle: 1,
      examSubject: 1,
      grade: 1,
      percentage: 1,
      totalPoints: 1,
      totalMaxPoints: 1,
      releasedAt: 1,
      attachedImageIds: 1,
      attachedPdfId: 1,
      tasks: { label: 1, description: 1, studentAnswer: 1, points: 1, maxPoints: 1, feedback: 1 },
    })
      .then(row => {
        if (isMounted) setResult(row ?? null);
      })
      .catch(err => logger.error(err, 'Failed to load exam result'))
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [examId]);

  const tasks = result?.tasks ?? [];
  const attachedImageIds = result?.attachedImageIds ?? [];
  const attachedPdfId = result?.attachedPdfId ?? null;
  const hasAttachment = attachedPdfId != null || attachedImageIds.length > 0;

  // A fresh page is shown at its natural size — zoom is a per-page choice, not a sticky one that would
  // otherwise crop the next page's edges the moment you page to it.
  const goToImage = (index: number) => {
    setActivePreview({ kind: 'image', index });
    setZoom(MIN_ZOOM);
  };

  return (
    <div className="flex flex-col h-full w-full bg-background overflow-y-auto">
      <div className="sticky top-0 z-10 border-b border-border bg-card px-4 sm:px-8 py-2 sm:py-4 shadow-sm">
        <div className="flex items-center gap-2 max-w-4xl mx-auto">
          <Button
            onClick={onBack}
            variant="ghost"
            size="icon"
            className="text-muted-foreground hover:text-foreground hover:bg-accent"
            aria-label={t('JoinClass.detailBack')}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-bold text-foreground tracking-tight truncate">
            {result?.examTitle ?? examTitle ?? ''}
          </h1>
        </div>
      </div>

      <div className="flex-1 px-4 sm:px-8 py-8">
        <div className="max-w-4xl mx-auto space-y-6 animate-in fade-in duration-500">
          {isLoading ? (
            <p className="text-sm text-muted-foreground">{t('JoinClass.loadingResults')}</p>
          ) : !result ? (
            <p className="text-sm italic text-muted-foreground/70">{t('JoinClass.result.notReleased')}</p>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { key: 'grade', value: result.grade ?? '—' },
                  {
                    key: 'points',
                    value:
                      result.totalPoints != null && result.totalMaxPoints != null
                        ? `${result.totalPoints} / ${result.totalMaxPoints}`
                        : '—',
                  },
                  { key: 'percent', value: result.percentage != null ? `${result.percentage.toFixed(0)}%` : '—' },
                ].map(({ key, value }) => (
                  <div
                    key={key}
                    className="flex items-center gap-3 p-4 bg-card border border-border rounded-lg shadow-sm"
                  >
                    <div className="w-10 h-10 bg-brand-surface rounded-lg flex items-center justify-center flex-shrink-0">
                      <Check className="h-5 w-5 text-brand-text" />
                    </div>
                    <div>
                      <p className="text-xl font-semibold text-foreground">{value}</p>
                      <p className="text-sm text-muted-foreground">{t(`JoinClass.result.${key}`)}</p>
                    </div>
                  </div>
                ))}
              </div>

              {hasAttachment && (
                <section className="bg-card border border-border rounded-xl p-5 shadow-sm">
                  <h2 className="text-sm font-semibold text-foreground mb-3">
                    {t('JoinClass.result.attachmentTitle')}
                  </h2>

                  {attachedPdfId && (
                    <Button
                      variant="outline"
                      size="sm"
                      className={attachedImageIds.length > 0 ? 'mb-3' : undefined}
                      onClick={() => setActivePreview({ kind: 'pdf' })}
                    >
                      <Eye className="h-4 w-4 mr-1.5" />
                      {t('JoinClass.result.viewPdf')}
                    </Button>
                  )}

                  {attachedImageIds.length > 0 && (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {attachedImageIds.map((imageId, index) => (
                        <button
                          key={imageId}
                          type="button"
                          onClick={() => goToImage(index)}
                          className={`block rounded-lg overflow-hidden transition-all ${
                            activePreview?.kind === 'image' && activePreview.index === index
                              ? 'ring-2 ring-brand-500'
                              : 'border border-border/60 hover:opacity-80'
                          }`}
                        >
                          <img
                            src={ImageFile.getServeLink(imageId)}
                            alt={t('JoinClass.result.pageAlt', { page: index + 1 })}
                            className="w-full h-auto"
                          />
                        </button>
                      ))}
                    </div>
                  )}
                </section>
              )}

              <section className="space-y-3">
                {tasks.length === 0 && (
                  <p className="text-sm italic text-muted-foreground/70">{t('JoinClass.result.noTaskDetail')}</p>
                )}
                {tasks.map((task, index) => (
                  <div key={`${task.label}-${index}`} className="bg-card border border-border rounded-xl p-5 shadow-sm">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <h3 className="font-semibold text-foreground">{task.label}</h3>
                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-brand-surface text-brand-text border border-brand-border shrink-0">
                        {task.points} / {task.maxPoints}
                      </span>
                    </div>

                    {task.description && (
                      <div className="prose prose-sm dark:prose-invert max-w-none text-muted-foreground mb-3">
                        <MarkdownWithMath>{task.description}</MarkdownWithMath>
                      </div>
                    )}

                    {task.studentAnswer && (
                      <div className="mb-3">
                        <p className="text-xs font-medium text-muted-foreground mb-1">
                          {t('JoinClass.result.yourAnswer')}
                        </p>
                        <div className="prose prose-sm dark:prose-invert max-w-none p-3 bg-background border border-border rounded-lg">
                          <MarkdownWithMath>{task.studentAnswer}</MarkdownWithMath>
                        </div>
                      </div>
                    )}

                    {task.feedback && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground mb-1">
                          {t('JoinClass.result.feedback')}
                        </p>
                        <div className="prose prose-sm dark:prose-invert max-w-none">
                          <MarkdownWithMath>{task.feedback}</MarkdownWithMath>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </section>
            </>
          )}
        </div>
      </div>

      <Dialog open={activePreview !== null} onOpenChange={open => !open && setActivePreview(null)}>
        <DialogContent className="sm:max-w-2xl bg-card text-card-foreground border-border gap-5">
          <DialogHeader>
            <DialogTitle className="font-medium">
              {activePreview?.kind === 'pdf'
                ? t('JoinClass.result.attachmentTitle')
                : t('JoinClass.result.pageAlt', { page: (activePreview?.index ?? 0) + 1 })}
            </DialogTitle>
          </DialogHeader>

          {activePreview?.kind === 'pdf' && attachedPdfId && (
            // `getServeLink`, not `getDownloadLink` — serve has no Content-Disposition: attachment
            // header, so the browser renders it inline instead of downloading it.
            <iframe
              src={PDFFile.getServeLink(attachedPdfId)}
              title={t('JoinClass.result.attachmentTitle')}
              className="w-full h-[60vh] rounded-lg border border-border/60"
            />
          )}

          {activePreview?.kind === 'image' && (
            <>
              <div className="flex items-center justify-end gap-1">
                <Button
                  variant="ghost"
                  size="icon-sm"
                  className="rounded-full hover:bg-gray-100 dark:hover:bg-white/10"
                  disabled={zoom <= MIN_ZOOM}
                  onClick={() => setZoom(z => Math.max(MIN_ZOOM, z - ZOOM_STEP))}
                  aria-label={t('JoinClass.result.zoomOut')}
                >
                  <ZoomOut className="h-4 w-4" />
                </Button>
                <span className="text-xs text-muted-foreground w-10 text-center tabular-nums">
                  {Math.round(zoom * 100)}%
                </span>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  className="rounded-full hover:bg-gray-100 dark:hover:bg-white/10"
                  disabled={zoom >= MAX_ZOOM}
                  onClick={() => setZoom(z => Math.min(MAX_ZOOM, z + ZOOM_STEP))}
                  aria-label={t('JoinClass.result.zoomIn')}
                >
                  <ZoomIn className="h-4 w-4" />
                </Button>
              </div>

              {/* `min-w-0` is load-bearing, not decorative: `DialogContent` is `display: grid`, and a grid
                  item's default `min-width` is `auto` — it will NOT shrink below its content's intrinsic
                  width unless something resets that. `overflow-auto` only resets it when set on the grid
                  item ITSELF; here it's one level down (on the scroll box below), so without this the
                  zoomed image just grows the grid track — and the whole dialog with it, up to its
                  `max-w-2xl` cap — instead of clipping and scrolling inside a fixed-size box. That's what
                  made zooming in look like it did nothing. */}
              <div className="relative min-w-0">
                <div className="max-h-[55vh] overflow-auto rounded-lg border border-border/60 bg-background">
                  <img
                    src={ImageFile.getServeLink(attachedImageIds[activePreview.index]!)}
                    alt={t('JoinClass.result.pageAlt', { page: activePreview.index + 1 })}
                    // `maxWidth: 'none'` is the actual fix, verified with a headless-browser repro:
                    // Tailwind's preflight sets `img { max-width: 100%; height: auto; }` globally, and
                    // `max-width` clamps the COMPUTED width regardless of how `width` was set — an inline
                    // style doesn't out-specificity it, because they aren't competing for the same
                    // property. Without this override, `width` below was silently capped at 100% no
                    // matter what `zoom` was, which is why zooming visibly did nothing.
                    style={{ width: `${zoom * 100}%`, maxWidth: 'none' }}
                    className="h-auto mx-auto transition-[width] duration-200"
                  />
                </div>

                {attachedImageIds.length > 1 && (
                  <>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-white/80 dark:bg-black/40 shadow-sm hover:bg-white dark:hover:bg-black/60"
                      disabled={activePreview.index === 0}
                      onClick={() => goToImage(activePreview.index - 1)}
                      aria-label={t('JoinClass.result.previousPage')}
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-white/80 dark:bg-black/40 shadow-sm hover:bg-white dark:hover:bg-black/60"
                      disabled={activePreview.index === attachedImageIds.length - 1}
                      onClick={() => goToImage(activePreview.index + 1)}
                      aria-label={t('JoinClass.result.nextPage')}
                    >
                      <ChevronRight className="h-5 w-5" />
                    </Button>
                  </>
                )}
              </div>

              {attachedImageIds.length > 1 && (
                <p className="text-center text-xs text-muted-foreground tabular-nums">
                  {t('JoinClass.result.pageCounter', {
                    current: activePreview.index + 1,
                    total: attachedImageIds.length,
                  })}
                </p>
              )}
            </>
          )}

          <DialogFooter>
            {activePreview?.kind === 'pdf' && attachedPdfId && (
              <Button
                asChild
                variant="ghost"
                size="sm"
                className="mr-auto font-medium hover:bg-gray-100 dark:hover:bg-white/10 transition-all"
              >
                <a href={PDFFile.getDownloadLink(attachedPdfId)} target="_blank" rel="noreferrer">
                  <Download className="h-4 w-4 mr-1.5" />
                  {t('JoinClass.result.downloadPdf')}
                </a>
              </Button>
            )}
            <Button
              variant="ghost"
              className="font-medium hover:bg-gray-100 dark:hover:bg-white/10 transition-all"
              onClick={() => setActivePreview(null)}
            >
              {t('JoinClass.result.closePreview')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
